/**
 * PulseFit Settings View Controller
 * 
 * Manages athlete preferences, weekly targets, heart rate BPM thresholds,
 * Gemini AI configuration, Strava connection visibility, and account deletion.
 */

import {
  store,
  saveStateToStorage,
  setGoals,
  setHrThresholds
} from '../../store.js';
import {
  stravaIdToUUID
} from '../integrations/index.js';

export function isStravaAdmin() {
  if (!store.currentUser) return false;
  const adminId = 'fd8660ed-8a5f-4426-8c43-ed5c096bb2fc';
  const adminEmails = ['support@ndclabs.app', 'nathan@test.com', 'nathan.cannon@gmail.com', 'nathan@pulsefit.local'];
  if (store.currentUser.id === adminId) return true;
  if (store.currentUser.email && adminEmails.includes(store.currentUser.email.toLowerCase().trim())) return true;
  return false;
}

export function updateStravaVisibility() {
  if (typeof document === 'undefined') return;
  const allowed = isStravaAdmin();
  const stravaCard = document.getElementById('card-strava-settings');
  if (stravaCard) {
    stravaCard.style.display = allowed ? 'flex' : 'none';
  }
  const postBtn = document.getElementById('btn-detail-post-strava');
  const repostBtn = document.getElementById('btn-detail-repost-strava');
  const inferLapsBtn = document.getElementById('btn-detail-infer-laps');
  if (postBtn && !allowed) postBtn.style.display = 'none';
  if (repostBtn && !allowed) repostBtn.style.display = 'none';
  if (inferLapsBtn && !allowed) inferLapsBtn.style.display = 'none';
}

let isStravaListenersBound = false;

export function setupStrava() {
  if (typeof document === 'undefined') return;
  updateStravaVisibility();
  if (!isStravaAdmin()) {
    return;
  }
  try {
    const clientIdInput = document.getElementById('strava-client-id');
    const clientSecretInput = document.getElementById('strava-client-secret');
    const redirectUriInput = document.getElementById('strava-redirect-uri');
    const callbackUrlInput = document.getElementById('strava-callback-url');

    const authorizeBtn = document.getElementById('btn-strava-authorize');
    const saveBtn = document.getElementById('btn-strava-save');
    const disconnectBtn = document.getElementById('btn-strava-disconnect');

    if (!clientIdInput || !clientSecretInput) {
      return;
    }

    const originUrl = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : 'http://localhost:8080';

    if (!store.strava || typeof store.strava !== 'object') {
      store.strava = {};
    }
    if (store.strava.syncMode === undefined) store.strava.syncMode = 'push_only';
    if (store.strava.clientId === undefined) store.strava.clientId = '';
    if (store.strava.clientSecret === undefined) store.strava.clientSecret = '';
    if (store.strava.redirectUri === undefined) store.strava.redirectUri = originUrl;
    if (store.strava.accessToken === undefined) store.strava.accessToken = '';
    if (store.strava.refreshToken === undefined) store.strava.refreshToken = '';
    if (store.strava.expiresAt === undefined) store.strava.expiresAt = 0;
    if (store.strava.athleteName === undefined) store.strava.athleteName = '';

    clientIdInput.value = store.strava.clientId || '';
    clientSecretInput.value = store.strava.clientSecret || '';
    if (redirectUriInput) {
      redirectUriInput.value = store.strava.redirectUri || originUrl;
    }

    if (!store.strava.redirectUri && typeof window !== 'undefined' && window.location && window.location.protocol && window.location.protocol.startsWith('http')) {
      store.strava.redirectUri = window.location.origin;
      if (redirectUriInput) redirectUriInput.value = store.strava.redirectUri;
    }

    updateStravaUI();

    if (isStravaListenersBound) return;
    isStravaListenersBound = true;

    const notifyCloud = () => {
      if (typeof window !== 'undefined' && typeof window.saveUserSettingsToCloud === 'function') {
        window.saveUserSettingsToCloud();
      }
    };
    const toast = (msg, type, dur) => {
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast(msg, type, dur);
      }
    };

    const pushModeBtn = document.getElementById('btn-strava-mode-push');
    const twowayModeBtn = document.getElementById('btn-strava-mode-twoway');
    if (pushModeBtn && twowayModeBtn) {
      pushModeBtn.addEventListener('click', () => {
        store.strava.syncMode = 'push_only';
        saveStateToStorage();
        notifyCloud();
        updateStravaUI();
        toast('Strava Integration Mode set to Push Only (Default)');
      });
      twowayModeBtn.addEventListener('click', () => {
        store.strava.syncMode = 'two_way';
        saveStateToStorage();
        notifyCloud();
        updateStravaUI();
        toast('Strava Integration Mode set to Two-Way (Push & Pull)');
      });
    }

    const headerSyncBtn = document.getElementById('btn-header-strava-sync');
    if (headerSyncBtn) {
      headerSyncBtn.addEventListener('click', async () => {
        try {
          const hasClientCreds = store.strava && store.strava.clientId && store.strava.clientSecret;
          const hasToken = store.strava && store.strava.accessToken;
          const isPushOnly = store.strava && store.strava.syncMode === 'push_only';

          if (isPushOnly) {
            toast('Strava is set to Push Only mode. Switch to Two-Way in Settings to import activities.', 'error', 3000);
            return;
          }

          if (!hasClientCreds) {
            toast('Redirecting to Settings to configure Strava credentials...', 'success', 2000);
            if (typeof window !== 'undefined' && typeof window.showView === 'function') window.showView('view-settings');
            return;
          }

          if (!hasToken) {
            toast('Redirecting to Settings to authorize Strava connection...', 'success', 2000);
            if (typeof window !== 'undefined' && typeof window.showView === 'function') window.showView('view-settings');
            return;
          }

          headerSyncBtn.innerText = 'Syncing...';
          headerSyncBtn.disabled = true;
          try {
            await syncStravaActivities();
          } catch (err) {
            if (typeof alert === 'function') alert('Sync failed: ' + err.message);
          } finally {
            headerSyncBtn.innerText = 'Sync Strava';
            headerSyncBtn.disabled = false;
            updateStravaUI();
          }
        } catch (e) {
          console.error('Error in headerSyncBtn click listener:', e);
        }
      });
    }

    if (authorizeBtn) {
      authorizeBtn.addEventListener('click', () => {
        try {
          const clientId = clientIdInput.value.trim();
          if (!clientId) {
            if (typeof alert === 'function') alert('Please enter your Strava Client ID first.');
            return;
          }

          let redirectURI = redirectUriInput ? redirectUriInput.value.trim() : '';
          if (!redirectURI) {
            redirectURI = originUrl;
          }

          if (redirectURI && !redirectURI.startsWith('http://') && !redirectURI.startsWith('https://')) {
            if (redirectURI.startsWith('localhost') || redirectURI.startsWith('127.0.0.1')) {
              redirectURI = 'http://' + redirectURI;
            } else {
              redirectURI = 'https://' + redirectURI;
            }
            if (redirectUriInput) redirectUriInput.value = redirectURI;
          }

          store.strava.clientId = clientId;
          store.strava.clientSecret = clientSecretInput.value.trim();
          store.strava.redirectUri = redirectURI;
          saveStateToStorage();
          notifyCloud();

          const authUrl = `https://www.strava.com/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectURI)}&response_type=code&scope=activity:write,activity:read_all`;
          if (typeof window !== 'undefined' && window.location) {
            window.location.href = authUrl;
          }
        } catch (e) {
          console.error('[Strava OAuth] Error in authorizeBtn click listener:', e);
        }
      });
    }

    if (saveBtn) {
      saveBtn.addEventListener('click', async () => {
        try {
          let redirectURI = redirectUriInput ? redirectUriInput.value.trim() : '';
          if (redirectURI && !redirectURI.startsWith('http://') && !redirectURI.startsWith('https://')) {
            if (redirectURI.startsWith('localhost') || redirectURI.startsWith('127.0.0.1')) {
              redirectURI = 'http://' + redirectURI;
            } else {
              redirectURI = 'https://' + redirectURI;
            }
            if (redirectUriInput) redirectUriInput.value = redirectURI;
          }

          store.strava.clientId = clientIdInput.value.trim();
          store.strava.clientSecret = clientSecretInput.value.trim();
          store.strava.redirectUri = redirectURI || originUrl;
          saveStateToStorage();
          notifyCloud();

          if (store.strava.accessToken) {
            saveBtn.innerText = 'Syncing...';
            saveBtn.disabled = true;
            try {
              await syncStravaActivities();
            } catch (err) {
              if (typeof alert === 'function') alert('Sync failed: ' + err.message);
            } finally {
              saveBtn.innerText = 'Save Settings';
              saveBtn.disabled = false;
            }
          } else {
            toast('Settings saved. Click Connect Account to authorize!');
          }
        } catch (e) {
          console.error('Error in saveBtn click listener:', e);
        }
      });
    }

    if (disconnectBtn) {
      disconnectBtn.addEventListener('click', () => {
        try {
          const confirmed = typeof confirm === 'function'
            ? confirm('Disconnect from Strava? This will remove credentials and tokens, but your logged workout history will remain.')
            : true;
          if (confirmed) {
            if (!store.strava) store.strava = {};
            store.strava.accessToken = '';
            store.strava.refreshToken = '';
            store.strava.expiresAt = 0;
            store.strava.athleteName = '';
            saveStateToStorage();
            notifyCloud();
            updateStravaUI();
            toast('Disconnected from Strava.');
          }
        } catch (e) {
          console.error('Error in disconnectBtn click listener:', e);
        }
      });
    }

    if (callbackUrlInput) {
      callbackUrlInput.addEventListener('input', async () => {
        try {
          const val = callbackUrlInput.value.trim();
          if (!val) return;

          if (!val.includes('code=')) {
            if (val.includes('.') || val.includes('localhost') || val.includes('http')) {
              let formattedUri = val;
              if (!formattedUri.startsWith('http://') && !formattedUri.startsWith('https://')) {
                formattedUri = (formattedUri.startsWith('localhost') || formattedUri.startsWith('127.0.0.1')) ? 'http://' + formattedUri : 'https://' + formattedUri;
              }
              if (redirectUriInput) redirectUriInput.value = formattedUri;
              store.strava.redirectUri = formattedUri;
              saveStateToStorage();
              notifyCloud();
              toast('Updated Redirect URI setting above! Click "Connect Account" to authorize.', 'success', 5000);
              callbackUrlInput.value = '';
            }
            return;
          }

          let code = null;
          try {
            const url = new URL(val);
            code = url.searchParams.get('code');
          } catch (e) {
            const match = val.match(/[?&]code=([^&]+)/);
            if (match) code = match[1];
          }

          if (!code) {
            toast('Could not find OAuth "code" parameter in the pasted URL. Please copy the full address after authorization.', 'error', 4000);
            callbackUrlInput.value = '';
            return;
          }

          callbackUrlInput.value = 'Processing code...';
          callbackUrlInput.disabled = true;

          try {
            await exchangeStravaCode(code);
            await syncStravaActivities();
            callbackUrlInput.value = '';
          } catch (err) {
            console.error('Strava connection failed:', err);
            updateStravaUI(err.message);
            callbackUrlInput.value = '';
          } finally {
            callbackUrlInput.disabled = false;
          }
        } catch (e) {
          console.error('Error in callbackUrlInput listener:', e);
        }
      });
    }

    if (typeof window !== 'undefined' && window.location && window.location.search) {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      if (code) {
        const cleanUrl = window.location.origin + window.location.pathname;
        if (window.history && typeof window.history.replaceState === 'function') {
          window.history.replaceState({}, document.title, cleanUrl);
        }

        setTimeout(async () => {
          try {
            if (typeof window.showView === 'function') window.showView('view-settings');
            if (callbackUrlInput) {
              callbackUrlInput.value = 'Exchanging code from URL...';
              callbackUrlInput.disabled = true;
            }
            await exchangeStravaCode(code);
            await syncStravaActivities();
            if (callbackUrlInput) {
              callbackUrlInput.value = '';
            }
          } catch (err) {
            console.error('Auto-sync failed:', err);
            updateStravaUI(err.message);
            if (callbackUrlInput) {
              callbackUrlInput.value = '';
            }
          } finally {
            if (callbackUrlInput) {
              callbackUrlInput.disabled = false;
            }
          }
        }, 500);
      }
    }
  } catch (globalErr) {
    console.error('Global exception in setupStrava():', globalErr);
  }
}

export function updateStravaUI(errorMsg = null) {
  if (typeof document === 'undefined') return;
  const statusText = document.getElementById('strava-status-text');
  const statusDetail = document.getElementById('strava-status-detail');
  const disconnectBtn = document.getElementById('btn-strava-disconnect');
  const syncBtn = document.getElementById('btn-header-strava-sync');

  if (!statusText) return;

  if (errorMsg) {
    statusText.innerText = 'Connection Error';
    statusText.className = 'status-disconnected';
    statusText.style.color = '#f43f5e';
    statusText.style.textShadow = '0 0 8px rgba(244, 63, 94, 0.3)';
    if (statusDetail) {
      statusDetail.innerText = errorMsg;
      statusDetail.style.display = 'block';
    }
    if (disconnectBtn) disconnectBtn.classList.remove('hidden');
    return;
  }

  statusText.style.color = '';
  statusText.style.textShadow = '';
  if (statusDetail) {
    statusDetail.innerText = '';
    statusDetail.style.display = 'none';
  }

  const pushModeBtn = document.getElementById('btn-strava-mode-push');
  const twowayModeBtn = document.getElementById('btn-strava-mode-twoway');
  const modeDesc = document.getElementById('strava-sync-mode-desc');
  const isPushOnly = !store.strava || store.strava.syncMode !== 'two_way';

  if (pushModeBtn && twowayModeBtn) {
    if (isPushOnly) {
      pushModeBtn.style.background = '#fc4c02';
      pushModeBtn.style.color = '#fff';
      pushModeBtn.classList.add('active');

      twowayModeBtn.style.background = 'transparent';
      twowayModeBtn.style.color = 'var(--text-muted)';
      twowayModeBtn.classList.remove('active');

      if (modeDesc) {
        modeDesc.innerText = 'Push Only (Default): Post RoxKit workouts to Strava. Importing activities from Strava is disabled.';
      }
    } else {
      twowayModeBtn.style.background = '#fc4c02';
      twowayModeBtn.style.color = '#fff';
      twowayModeBtn.classList.add('active');

      pushModeBtn.style.background = 'transparent';
      pushModeBtn.style.color = 'var(--text-muted)';
      pushModeBtn.classList.remove('active');

      if (modeDesc) {
        modeDesc.innerText = 'Two-Way: Post workouts to Strava AND pull/import activities from Strava into RoxKit.';
      }
    }
  }

  if (store.strava && store.strava.accessToken) {
    statusText.innerText = `Connected as ${store.strava.athleteName || 'Athlete'}`;
    statusText.className = 'status-connected';
    if (disconnectBtn) disconnectBtn.classList.remove('hidden');
    if (syncBtn) {
      if (isPushOnly) {
        syncBtn.disabled = true;
        syncBtn.style.opacity = '0.4';
        syncBtn.style.cursor = 'not-allowed';
        syncBtn.style.borderColor = 'rgba(255,255,255,0.15)';
        syncBtn.style.color = 'var(--text-muted)';
        syncBtn.title = 'Strava is in Push-Only mode. Switch to Two-Way in Settings to sync.';
      } else {
        syncBtn.disabled = false;
        syncBtn.style.opacity = '1';
        syncBtn.style.cursor = 'pointer';
        syncBtn.style.borderColor = '#fc4c02';
        syncBtn.style.color = '#fc4c02';
        syncBtn.title = 'Sync Strava Activities';
      }
    }
  } else {
    statusText.innerText = 'Not Connected';
    statusText.className = 'status-disconnected';
    if (disconnectBtn) disconnectBtn.classList.add('hidden');
    if (syncBtn) {
      syncBtn.disabled = isPushOnly;
      syncBtn.style.opacity = isPushOnly ? '0.4' : '1';
      syncBtn.style.cursor = isPushOnly ? 'not-allowed' : 'pointer';
      syncBtn.style.borderColor = '';
      syncBtn.style.color = '';
    }
  }
}

export async function exchangeStravaCode(code) {
  const clientId = store.strava?.clientId;
  const clientSecret = store.strava?.clientSecret;
  const redirectUri = store.strava?.redirectUri;

  if (!clientId || !clientSecret) {
    throw new Error('Client ID and Client Secret are required for exchange.');
  }

  const response = await fetch('https://www.strava.com/api/v3/oauth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      client_id: clientId,
      client_secret: clientSecret,
      code: code,
      grant_type: 'authorization_code',
      redirect_uri: redirectUri
    })
  });

  if (!response.ok) {
    let errMsg = 'Token exchange failed';
    try {
      const text = await response.text();
      try {
        const errData = JSON.parse(text);
        if (errData.message) {
          errMsg = errData.message;
          if (errData.errors && errData.errors.length > 0) {
            const detail = errData.errors.map(e => `${e.field}: ${e.code}`).join(', ');
            errMsg += ` (${detail})`;
          }
        } else if (errData.error) {
          errMsg = errData.error;
        } else {
          errMsg = text.substring(0, 200);
        }
      } catch (jsonErr) {
        errMsg = `HTTP error ${response.status}: ${text.substring(0, 200)}`;
      }
    } catch (e) {
      errMsg = `HTTP error ${response.status}: ${response.statusText}`;
    }
    throw new Error(errMsg);
  }

  const data = await response.json();
  store.strava.accessToken = data.access_token;
  store.strava.refreshToken = data.refresh_token;
  store.strava.expiresAt = data.expires_at;
  store.strava.athleteName = data.athlete ? `${data.athlete.firstname || ''} ${data.athlete.lastname || ''}`.trim() : 'Athlete';
  saveStateToStorage();
  if (typeof window !== 'undefined' && typeof window.saveUserSettingsToCloud === 'function') {
    window.saveUserSettingsToCloud();
  }
  updateStravaUI();
}

export async function refreshStravaToken() {
  if (!store.strava) return;
  const currentTime = Math.floor(Date.now() / 1000);
  const expiresAt = store.strava.expiresAt;
  const secondsRemaining = expiresAt ? (expiresAt - currentTime) : 0;

  if (expiresAt && secondsRemaining > 300) {
    return;
  }

  const clientIdInput = typeof document !== 'undefined' ? document.getElementById('strava-client-id') : null;
  const clientSecretInput = typeof document !== 'undefined' ? document.getElementById('strava-client-secret') : null;
  const resolvedClientId = String(store.strava.clientId || (clientIdInput && clientIdInput.value) || '').trim();
  const resolvedClientSecret = String(store.strava.clientSecret || (clientSecretInput && clientSecretInput.value) || '').trim();

  if (!resolvedClientId || !resolvedClientSecret) {
    throw new Error('Strava Client ID or Client Secret is missing. Please enter and save your Strava credentials in Settings.');
  }

  const response = await fetch('https://www.strava.com/api/v3/oauth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      client_id: resolvedClientId,
      client_secret: resolvedClientSecret,
      refresh_token: store.strava.refreshToken,
      grant_type: 'refresh_token'
    })
  });

  if (!response.ok) {
    let errText = '';
    try { errText = await response.text(); } catch (e) {}
    throw new Error(`Failed to refresh Strava token (HTTP ${response.status}: ${errText.substring(0, 100)})`);
  }

  const data = await response.json();
  store.strava.clientId = resolvedClientId;
  store.strava.clientSecret = resolvedClientSecret;
  store.strava.accessToken = data.access_token;
  store.strava.refreshToken = data.refresh_token;
  store.strava.expiresAt = data.expires_at;
  saveStateToStorage();
  if (typeof window !== 'undefined' && typeof window.saveUserSettingsToCloud === 'function') {
    window.saveUserSettingsToCloud();
  }
}

export async function syncStravaActivities() {
  if (store.strava && store.strava.syncMode === 'push_only') {
    return;
  }

  if (!store.strava || !store.strava.accessToken) {
    throw new Error('No Strava access token. Please connect your Strava account in Settings.');
  }

  await refreshStravaToken();

  const refDate = store.currentWeekStart ? new Date(store.currentWeekStart) : new Date();
  const monday = new Date(refDate);
  monday.setHours(0, 0, 0, 0);
  const afterEpoch = Math.floor(monday.getTime() / 1000);

  const nextMonday = new Date(monday);
  nextMonday.setDate(monday.getDate() + 7);
  nextMonday.setHours(0, 0, 0, 0);
  const beforeEpoch = Math.floor(nextMonday.getTime() / 1000);

  const activitiesUrl = `https://www.strava.com/api/v3/athlete/activities?before=${beforeEpoch}&after=${afterEpoch}&per_page=50`;
  const response = await fetch(activitiesUrl, {
    headers: {
      'Authorization': `Bearer ${store.strava.accessToken}`
    }
  });

  if (!response.ok) {
    let errText = '';
    try { errText = await response.text(); } catch (e) {}
    throw new Error(`Failed to fetch Strava activities (HTTP ${response.status}: ${errText.substring(0, 100)})`);
  }

  const activities = await response.json();
  let newCount = 0;
  const mappedActivities = [];
  const supabase = (typeof window !== 'undefined' && window.supabaseClient) || store.supabaseClient;

  for (const act of activities) {
    const isDuplicate = (store.workouts || []).some(w => w.stravaId && String(w.stravaId) === String(act.id));
    if (isDuplicate) continue;

    let type = 'Other';
    if (act.type === 'Run') type = 'Run';
    else if (act.type === 'Ride' || act.type === 'VirtualRide') type = 'Cycle';
    else if (act.type === 'WeightTraining' || act.type === 'Workout') type = 'Strength';
    else if (act.type === 'Walk') type = 'Walk';
    else if (act.type === 'Yoga') type = 'Yoga';
    else if (act.type === 'Swim') type = 'Swim';

    const durationMin = Math.round((act.moving_time || 0) / 60);
    let calories = act.calories || act.kilojoules || 0;

    if (!calories) {
      try {
        const detailResp = await fetch(`https://www.strava.com/api/v3/activities/${act.id}`, {
          headers: { 'Authorization': `Bearer ${store.strava.accessToken}` }
        });
        if (detailResp.ok) {
          const detailedAct = await detailResp.json();
          calories = detailedAct.calories || detailedAct.kilojoules || 0;
        }
      } catch (e) {
        console.error('Failed to fetch detailed Strava activity for calories:', e);
      }
    }

    if (!calories) {
      let factor = 5;
      if (type === 'Run') factor = 10;
      else if (type === 'Cycle') factor = 7;
      else if (type === 'Swim') factor = 8;
      else if (type === 'Strength') factor = 5;
      else if (type === 'Yoga') factor = 3;
      calories = Math.round(durationMin * factor);
    }

    const activityDate = new Date(act.start_date);
    const dateFormatted = activityDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timeFormatted = activityDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    let distance = null;
    if (act.distance) {
      distance = Math.round((act.distance / 1000) * 100) / 100;
    }

    const elapsedMin = act.elapsed_time ? Math.round(act.elapsed_time / 60) : null;
    const elevationVal = act.total_elevation_gain ? Math.round(act.total_elevation_gain) : null;
    const avgHrVal = act.has_heartrate ? Math.round(act.average_heartrate) : null;
    const maxHrVal = act.has_heartrate ? Math.round(act.max_heartrate) : null;
    const sufferVal = act.suffer_score || null;
    const deviceNameStr = act.device_name || null;
    const kudosCountVal = act.kudos_count || 0;
    const commentCountVal = act.comment_count || 0;
    const achievementCountVal = act.achievement_count || 0;
    const maxSpeedVal = act.max_speed || null;
    const avgWattsVal = act.average_watts || null;
    const avgCadenceVal = act.average_cadence || null;
    const kilojoulesVal = act.kilojoules || null;
    const elevHighVal = act.elev_high !== undefined ? act.elev_high : null;
    const elevLowVal = act.elev_low !== undefined ? act.elev_low : null;
    const prCountVal = act.pr_count || 0;
    const athleteCountVal = act.athlete_count || 1;
    const commuteFlag = act.commute || false;
    const trainerFlag = act.trainer || false;
    const privateFlag = act.private || false;
    const polylineStr = act.map ? act.map.summary_polyline : null;

    let zonesObj = null;
    if (act.has_heartrate) {
      try {
        const zonesResp = await fetch(`https://www.strava.com/api/v3/activities/${act.id}/zones`, {
          headers: { 'Authorization': `Bearer ${store.strava.accessToken}` }
        });
        if (zonesResp.ok) {
          const zonesData = await zonesResp.json();
          const hrZones = zonesData.find(z => z.type === 'heartrate');
          if (hrZones && hrZones.distribution_buckets) {
            zonesObj = hrZones.distribution_buckets.map(b => b.time);
          }
        }
      } catch (e) {
        console.error('Failed to fetch zones for sync:', e);
      }
    }

    const newWorkout = {
      id: stravaIdToUUID(act.id),
      timestamp: activityDate.getTime(),
      stravaId: act.id,
      date: dateFormatted,
      time: timeFormatted,
      type: type,
      duration: durationMin,
      calories: calories,
      notes: act.name ? `Synced: "${act.name}"` : 'Synced from Strava',
      distance: distance,
      elapsedTime: elapsedMin,
      elevationGain: elevationVal,
      avgHeartrate: avgHrVal,
      maxHeartrate: maxHrVal,
      sufferScore: sufferVal,
      deviceName: deviceNameStr,
      kudosCount: kudosCountVal,
      commentCount: commentCountVal,
      achievementCount: achievementCountVal,
      maxSpeed: maxSpeedVal,
      averageWatts: avgWattsVal,
      averageCadence: avgCadenceVal,
      kilojoules: kilojoulesVal,
      elevHigh: elevHighVal,
      elevLow: elevLowVal,
      prCount: prCountVal,
      athleteCount: athleteCountVal,
      commute: commuteFlag,
      trainer: trainerFlag,
      private: privateFlag,
      polyline: polylineStr,
      zones: zonesObj,
      excludeFromStats: false
    };

    store.workouts.unshift(newWorkout);
    if (supabase && store.currentUser && store.currentUser.id && !store.isGuest && store.currentUser.id !== 'guest-preview-user') {
      const startMs = newWorkout.timestamp || Date.now();
      const dbWorkout = {
        id: newWorkout.id,
        user_id: store.currentUser.id,
        created_at: new Date(startMs).toISOString(),
        activity_type: newWorkout.type || 'Other',
        duration_seconds: Math.round(durationMin * 60),
        start_time: new Date(startMs).toISOString(),
        end_time: new Date(startMs + durationMin * 60 * 1000).toISOString(),
        total_distance_meters: Math.round((newWorkout.distance || 0) * 1000),
        total_energy_burned_kcal: Math.round(newWorkout.calories || 0),
        source_name: 'Strava',
        source_version: '1.4',
        healthkit_raw_metadata: {
          zones: newWorkout.zones,
          stages: newWorkout.stages,
          avgHeartrate: newWorkout.avgHeartrate,
          maxHeartrate: newWorkout.maxHeartrate,
          sufferScore: newWorkout.sufferScore,
          notes: newWorkout.notes,
          excludeFromStats: newWorkout.excludeFromStats,
          stravaId: newWorkout.stravaId,
          elapsedTime: newWorkout.elapsedTime,
          elevationGain: newWorkout.elevationGain,
          polyline: newWorkout.polyline,
          subjective: newWorkout.subjective || null,
          aiSummary: newWorkout.aiSummary || null
        }
      };
      mappedActivities.push(dbWorkout);
    }
    newCount++;
  }

  if (newCount > 0) {
    if (supabase && store.currentUser && mappedActivities.length > 0) {
      try {
        const { error } = await supabase.from('workouts').upsert(mappedActivities, { onConflict: 'id' });
        if (error) throw error;
      } catch (err) {
        console.error('Database error syncing Strava activities:', err);
      }
    }
    saveStateToStorage();
    if (typeof window !== 'undefined') {
      if (typeof window.renderDashboard === 'function') window.renderDashboard();
      if (typeof window.renderWorkouts === 'function') window.renderWorkouts();
      if (typeof window.renderAchievements === 'function') window.renderAchievements();
      if (typeof window.showToast === 'function') window.showToast(`Sync complete! Loaded ${newCount} new workouts.`, 'success', 1500);
    }
  } else {
    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast('Sync complete! No new workouts found.', 'success', 1500);
    }
  }

  if (typeof window !== 'undefined' && typeof window.requestHealthKitWorkouts === 'function') {
    window.requestHealthKitWorkouts();
  }
}

export function populateSettingsGoalsFields() {
  if (typeof document === 'undefined') return;

  if (!store.goals) store.goals = { suffer: 550, distance: 25.0, calories: 3500, stations: {} };
  if (!store.weeklyZoneGoals) store.weeklyZoneGoals = { z2: 120, z3: 60, z4: 60, z5: 30 };
  if (!store.hrThresholds) store.hrThresholds = { z2Min: 125, z3Min: 141, z4Min: 156, z5Min: 170 };

  const FIELD_MAP = [
    { id: 'goal-suffer',              get: () => store.goals.suffer },
    { id: 'goal-distance',            get: () => store.goals.distance },
    { id: 'goal-calories',            get: () => store.goals.calories },
    { id: 'goal-z2',                  get: () => (store.weeklyZoneGoals || {}).z2 },
    { id: 'goal-z3',                  get: () => (store.weeklyZoneGoals || {}).z3 },
    { id: 'goal-z4',                  get: () => (store.weeklyZoneGoals || {}).z4 },
    { id: 'goal-z5',                  get: () => (store.weeklyZoneGoals || {}).z5 },
    { id: 'goal-st-SkiErg',           get: () => (store.goals.stations || {})['SkiErg'] },
    { id: 'goal-st-SledPush',         get: () => (store.goals.stations || {})['Sled Push'] },
    { id: 'goal-st-SledPull',         get: () => (store.goals.stations || {})['Sled Pull'] },
    { id: 'goal-st-BurpeeBroadJumps', get: () => (store.goals.stations || {})['Burpee Broad Jumps'] },
    { id: 'goal-st-Rowing',           get: () => (store.goals.stations || {})['Rowing'] },
    { id: 'goal-st-FarmersCarry',     get: () => (store.goals.stations || {})['Farmers Carry'] },
    { id: 'goal-st-SandbagLunges',    get: () => (store.goals.stations || {})['Sandbag Lunges'] },
    { id: 'goal-st-WallBalls',        get: () => (store.goals.stations || {})['Wall Balls'] },
  ];

  FIELD_MAP.forEach(f => {
    const el = document.getElementById(f.id);
    if (!el) return;
    const val = f.get();
    if (val !== undefined && val !== null) el.value = val;
  });

  populateHeartRateThresholds();
}

export function populateHeartRateThresholds() {
  if (typeof document === 'undefined') return;
  const hz = store.hrThresholds || {};
  const z2El = document.getElementById('input-hr-z2-min');
  if (z2El && hz.z2Min) z2El.value = hz.z2Min;
  const z3El = document.getElementById('input-hr-z3-min');
  if (z3El && hz.z3Min) z3El.value = hz.z3Min;
  const z4El = document.getElementById('input-hr-z4-min');
  if (z4El && hz.z4Min) z4El.value = hz.z4Min;
  const z5El = document.getElementById('input-hr-z5-min');
  if (z5El && hz.z5Min) z5El.value = hz.z5Min;
}

export function saveSettings() {
  if (typeof document === 'undefined') return;

  const FIELD_MAP = [
    { id: 'goal-suffer',              set: (v) => { store.goals.suffer = v; }, parse: parseFloat },
    { id: 'goal-distance',            set: (v) => { store.goals.distance = v; }, parse: parseFloat },
    { id: 'goal-calories',            set: (v) => { store.goals.calories = v; }, parse: parseFloat },
    { id: 'goal-z2',                  set: (v) => { if (!store.weeklyZoneGoals) store.weeklyZoneGoals={}; store.weeklyZoneGoals.z2 = v; }, parse: parseInt },
    { id: 'goal-z3',                  set: (v) => { if (!store.weeklyZoneGoals) store.weeklyZoneGoals={}; store.weeklyZoneGoals.z3 = v; }, parse: parseInt },
    { id: 'goal-z4',                  set: (v) => { if (!store.weeklyZoneGoals) store.weeklyZoneGoals={}; store.weeklyZoneGoals.z4 = v; }, parse: parseInt },
    { id: 'goal-z5',                  set: (v) => { if (!store.weeklyZoneGoals) store.weeklyZoneGoals={}; store.weeklyZoneGoals.z5 = v; }, parse: parseInt },
    { id: 'goal-st-SkiErg',           set: (v) => { store.goals.stations['SkiErg'] = v; }, parse: parseInt },
    { id: 'goal-st-SledPush',         set: (v) => { store.goals.stations['Sled Push'] = v; }, parse: parseInt },
    { id: 'goal-st-SledPull',         set: (v) => { store.goals.stations['Sled Pull'] = v; }, parse: parseInt },
    { id: 'goal-st-BurpeeBroadJumps', set: (v) => { store.goals.stations['Burpee Broad Jumps'] = v; }, parse: parseInt },
    { id: 'goal-st-Rowing',           set: (v) => { store.goals.stations['Rowing'] = v; }, parse: parseInt },
    { id: 'goal-st-FarmersCarry',     set: (v) => { store.goals.stations['Farmers Carry'] = v; }, parse: parseInt },
    { id: 'goal-st-SandbagLunges',    set: (v) => { store.goals.stations['Sandbag Lunges'] = v; }, parse: parseInt },
    { id: 'goal-st-WallBalls',        set: (v) => { store.goals.stations['Wall Balls'] = v; }, parse: parseInt },
  ];

  if (!store.goals) store.goals = {};
  if (!store.goals.stations) store.goals.stations = {};

  let valid = true;
  FIELD_MAP.forEach(f => {
    const el = document.getElementById(f.id);
    if (!el) return;
    const raw = el.value.trim();
    if (raw === '') return;
    const val = f.parse(raw, 10);
    if (isNaN(val) || val < 0) {
      el.style.borderColor = '#f43f5e';
      valid = false;
      return;
    }
    el.style.borderColor = '';
    f.set(val);
  });

  if (!valid) {
    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast('Please fix invalid values (highlighted in red).', 'error');
    }
    return;
  }

  const z2Min = parseInt(document.getElementById('input-hr-z2-min')?.value || 125, 10);
  const z3Min = parseInt(document.getElementById('input-hr-z3-min')?.value || 141, 10);
  const z4Min = parseInt(document.getElementById('input-hr-z4-min')?.value || 156, 10);
  const z5Min = parseInt(document.getElementById('input-hr-z5-min')?.value || 170, 10);

  setHrThresholds({ z2Min, z3Min, z4Min, z5Min });
  saveStateToStorage();

  if (typeof window !== 'undefined') {
    if (typeof window.saveUserSettingsToCloud === 'function') window.saveUserSettingsToCloud();
    if (typeof window.renderDashboard === 'function') window.renderDashboard();
    if (typeof window.showToast === 'function') window.showToast('✅ Goals and HR zone targets saved successfully!');
  }
}

export function setupGoalsEditor() {
  if (typeof document === 'undefined') return;

  const tabBtns = document.querySelectorAll('.goals-tab-btn');
  tabBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      tabBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const targetTab = btn.getAttribute('data-goals-tab');
      
      ['dashboard', 'zones', 'hyrox'].forEach(s => {
        const secEl = document.getElementById(`goals-section-${s}`);
        if (secEl) {
          secEl.style.display = (s === targetTab) ? 'block' : 'none';
        }
      });
    });
  });

  populateSettingsGoalsFields();

  const saveBtn = document.getElementById('btn-save-goals');
  if (saveBtn) {
    saveBtn.addEventListener('click', () => saveSettings());
  }

  const saveHrZonesBtn = document.getElementById('btn-save-hr-zones');
  if (saveHrZonesBtn) {
    saveHrZonesBtn.addEventListener('click', () => {
      const z2Val = parseInt(document.getElementById('input-hr-z2-min')?.value || 125, 10);
      const z3Val = parseInt(document.getElementById('input-hr-z3-min')?.value || 141, 10);
      const z4Val = parseInt(document.getElementById('input-hr-z4-min')?.value || 156, 10);
      const z5Val = parseInt(document.getElementById('input-hr-z5-min')?.value || 170, 10);

      setHrThresholds({ z2Min: z2Val, z3Min: z3Val, z4Min: z4Val, z5Min: z5Val });
      saveStateToStorage();

      if (typeof window !== 'undefined') {
        if (typeof window.saveUserSettingsToCloud === 'function') window.saveUserSettingsToCloud();
        if (typeof window.renderDashboard === 'function') window.renderDashboard();
        if (typeof window.showToast === 'function') window.showToast('✅ HR zone thresholds saved!');
      }
    });
  }
}

export function populateGeminiFields() {
  if (typeof document === 'undefined') return;
  const inputKey = document.getElementById('input-gemini-key');
  const inputInstructions = document.getElementById('input-gemini-instructions');
  
  if (inputKey && store.geminiApiKey) {
    inputKey.value = store.geminiApiKey;
  }
  if (inputInstructions && store.geminiInstructions) {
    inputInstructions.value = store.geminiInstructions;
  }
}

export function setupGemini() {
  if (typeof document === 'undefined') return;
    
  populateGeminiFields();

  const saveBtn = document.getElementById('btn-save-gemini-settings');
  if (saveBtn) {
    saveBtn.addEventListener('click', () => {
      const inputKey = document.getElementById('input-gemini-key');
      const inputInstructions = document.getElementById('input-gemini-instructions');
      if (inputKey) store.geminiApiKey = inputKey.value.trim();
      if (inputInstructions) store.geminiInstructions = inputInstructions.value.trim();
      saveStateToStorage();
      if (typeof window !== 'undefined' && typeof window.saveUserSettingsToCloud === 'function') {
        window.saveUserSettingsToCloud();
      }
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast('✅ AI Settings saved successfully!');
      }
    });
  }

  const closeBtn = document.getElementById('close-ai-btn');
  const closeBtn2 = document.getElementById('btn-close-ai-dialog');
  const dialog = document.getElementById('ai-summary-dialog');
  
  if (dialog) {
    if (closeBtn) closeBtn.addEventListener('click', () => dialog.close());
    if (closeBtn2) closeBtn2.addEventListener('click', () => dialog.close());
  }
}

export function openDeleteAccountDialog() {
  if (typeof document === 'undefined') return;
  const dialog = document.getElementById('dialog-confirm-delete-account');
  if (dialog && typeof dialog.showModal === 'function') {
    dialog.showModal();
  }
}

export function closeDeleteAccountDialog() {
  if (typeof document === 'undefined') return;
  const dialog = document.getElementById('dialog-confirm-delete-account');
  if (dialog && typeof dialog.close === 'function') {
    dialog.close();
  }
}

export async function executeAccountDeletion() {
  const supabase = (typeof window !== 'undefined') ? window.supabaseClient : null;
  const btnDelete = document.getElementById('btn-settings-delete-account');
  const btnConfirmModal = document.getElementById('btn-confirm-delete-account');
  if (btnDelete) {
    btnDelete.disabled = true;
    btnDelete.innerText = 'Deleting...';
  }
  if (btnConfirmModal) {
    btnConfirmModal.disabled = true;
    btnConfirmModal.innerText = 'Deleting...';
  }

  try {
    if (store.isGuest) {
      if (typeof window !== 'undefined' && typeof window.clearAllLocalUserData === 'function') {
        window.clearAllLocalUserData();
      }
      closeDeleteAccountDialog();
      const authView = document.getElementById('view-auth');
      const mainContainer = document.getElementById('main-app-container');
      if (authView) authView.style.display = 'flex';
      if (mainContainer) mainContainer.style.display = 'none';
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast('Guest session cleared', 'success');
      }
      return;
    }

    if (supabase && store.currentUser && store.currentUser.id) {
      const userId = store.currentUser.id;
      const { error: dbError } = await supabase
        .from('workouts')
        .delete()
        .eq('user_id', userId);

      if (dbError) {
        console.warn("Error deleting workouts from Supabase:", dbError.message);
      }

      const { error: plannedError } = await supabase
        .from('planned_workouts')
        .delete()
        .eq('user_id', userId);

      if (plannedError) {
        console.warn("Error deleting planned_workouts from Supabase:", plannedError.message);
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (profileError) {
        console.warn("Error deleting profile from Supabase:", profileError.message);
      }

      if (typeof supabase.rpc === 'function') {
        const { error: rpcError } = await supabase.rpc('delete_own_account');
        if (rpcError) {
          console.warn("Error calling delete_own_account RPC:", rpcError.message);
          if (typeof fetch === 'function' && store.session && store.session.access_token) {
            await fetch('/api/account', {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${store.session.access_token}` }
            }).catch(() => {});
          }
        }
      }
    }

    if (typeof window !== 'undefined' && typeof window.clearAllLocalUserData === 'function') {
      window.clearAllLocalUserData();
    }

    if (typeof window !== 'undefined' && window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.authHandler) {
      window.webkit.messageHandlers.authHandler.postMessage({ action: 'accountDeleted' });
      window.webkit.messageHandlers.authHandler.postMessage({ action: 'logout' });
    }

    if (supabase) {
      await supabase.auth.signOut().catch(e => console.warn("SignOut error:", e));
    }

    closeDeleteAccountDialog();

    const authView = document.getElementById('view-auth');
    const mainContainer = document.getElementById('main-app-container');
    if (authView) authView.style.display = 'flex';
    if (mainContainer) mainContainer.style.display = 'none';
    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast('Your account and workout telemetry have been permanently deleted.', 'success');
    }
  } catch (err) {
    console.error("Account deletion failed:", err);
    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(`Error deleting account: ${err.message}`, 'error');
    }
  } finally {
    if (btnDelete) {
      btnDelete.disabled = false;
      btnDelete.innerText = 'Delete Account';
    }
    if (btnConfirmModal) {
      btnConfirmModal.disabled = false;
      btnConfirmModal.innerText = 'Delete My Account';
    }
    closeDeleteAccountDialog();
  }
}

let isSettingsInitialized = false;

export function initSettingsView() {
  if (isSettingsInitialized) return;
  isSettingsInitialized = true;
  setupGoalsEditor();
  setupGemini();
  setupStrava();

  const btnDelete = document.getElementById('btn-settings-delete-account');
  if (btnDelete) {
    btnDelete.addEventListener('click', () => openDeleteAccountDialog());
  }

  const btnCancelDelete = document.getElementById('btn-cancel-delete-account');
  if (btnCancelDelete) {
    btnCancelDelete.addEventListener('click', () => closeDeleteAccountDialog());
  }

  const btnCloseDelete = document.getElementById('close-delete-account-dialog-btn');
  if (btnCloseDelete) {
    btnCloseDelete.addEventListener('click', () => closeDeleteAccountDialog());
  }

  const btnConfirmDelete = document.getElementById('btn-confirm-delete-account');
  if (btnConfirmDelete) {
    btnConfirmDelete.addEventListener('click', () => executeAccountDeletion());
  }
}
